import { useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Pencil, Plus, Search, Trash2, Truck, Wallet, X } from 'lucide-react'
import {
  formatDateTime, formatMoney, formatNumber, PERMISSIONS, supplierSchema, toMajor, toMinor,
  type SupplierListItem,
} from '@najah/core'
import {
  Alert, Badge, Button, ConfirmDialog, Dialog, EmptyState, Input, Pagination, Switch, Textarea,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { suppliersApi, type SupplierDetail } from '../lib/purchasingApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

interface FormState {
  code: string
  nameAr: string
  contactName: string
  phone: string
  email: string
  address: string
  notes: string
  openingBalance: string
  isActive: boolean
}

const EMPTY: FormState = {
  code: '', nameAr: '', contactName: '', phone: '', email: '',
  address: '', notes: '', openingBalance: '', isActive: true,
}

function SupplierFormDialog({
  open, onClose, supplier,
}: { open: boolean; onClose: () => void; supplier: SupplierDetail | null }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()
  const [form, setForm] = useState<FormState>(EMPTY)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  useEffect(() => {
    if (!open) return
    clear()
    setForm(
      supplier
        ? {
            code: supplier.code,
            nameAr: supplier.nameAr,
            contactName: supplier.contactName ?? '',
            phone: supplier.phone ?? '',
            email: supplier.email ?? '',
            address: supplier.address ?? '',
            notes: supplier.notes ?? '',
            openingBalance: supplier.openingBalance ? String(toMajor(supplier.openingBalance)) : '',
            isActive: supplier.isActive,
          }
        : EMPTY,
    )
  }, [open, supplier, clear])

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = validate(supplierSchema, {
        code: form.code.trim(),
        nameAr: form.nameAr.trim(),
        contactName: form.contactName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        openingBalance: toMinor(Number(form.openingBalance) || 0),
        isActive: form.isActive,
      })
      if (!parsed) throw new Error('VALIDATION')
      return supplier ? suppliersApi.update(supplier.id, parsed) : suppliersApi.create(parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      toast.success(supplier ? 'تم حفظ المورّد' : 'تمت إضافة المورّد')
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ المورّد')
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={supplier ? `تعديل: ${supplier.nameAr}` : 'مورّد جديد'}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>حفظ</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="اسم المورّد" required autoFocus
            value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)}
            error={errors.nameAr}
          />
          <Input
            label="الرمز" required ltr
            value={form.code} onChange={(e) => set('code', e.target.value)}
            error={errors.code} hint="رمز مختصر مثل DAR-HODA"
          />
          <Input
            label="اسم المسؤول"
            value={form.contactName} onChange={(e) => set('contactName', e.target.value)}
            error={errors.contactName}
          />
          <Input
            label="الهاتف" ltr
            value={form.phone} onChange={(e) => set('phone', e.target.value)}
            error={errors.phone}
          />
          <Input
            label="البريد الإلكتروني" type="email" ltr
            value={form.email} onChange={(e) => set('email', e.target.value)}
            error={errors.email}
          />
          <Input
            label="رصيد افتتاحي" type="number" min="0" step="0.01" ltr
            value={form.openingBalance} onChange={(e) => set('openingBalance', e.target.value)}
            error={errors.openingBalance}
            hint="دين قائم علينا قبل استعمال النظام"
          />
        </div>

        <Input
          label="العنوان"
          value={form.address} onChange={(e) => set('address', e.target.value)}
          error={errors.address}
        />
        <Textarea
          label="ملاحظات" rows={2}
          value={form.notes} onChange={(e) => set('notes', e.target.value)}
          error={errors.notes}
        />
        <Switch
          checked={form.isActive}
          onChange={(e) => set('isActive', e.target.checked)}
          label="مورّد نشط"
          description="غير النشط لا يظهر في فواتير الشراء الجديدة"
        />
      </div>
    </Dialog>
  )
}

function StatementDialog({ supplierId, onClose }: { supplierId: string | null; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['suppliers', 'statement', supplierId],
    queryFn: () => suppliersApi.statement(supplierId!),
    enabled: !!supplierId,
  })

  return (
    <Dialog
      open={!!supplierId}
      onClose={onClose}
      size="xl"
      title={data ? `كشف حساب: ${data.supplier.nameAr}` : 'كشف الحساب'}
      description={data ? `الرصيد الحالي ${formatMoney(data.balance)}` : undefined}
    >
      {data && (
        <div className="space-y-4">
          <Alert tone={data.balance > 0 ? 'warning' : 'success'}>
            {data.balance > 0 ? (
              <>مستحق للمورّد علينا: <span className="font-semibold" dir="ltr">{formatMoney(data.balance)}</span></>
            ) : data.balance < 0 ? (
              <>دفعنا زيادة: <span className="font-semibold" dir="ltr">{formatMoney(Math.abs(data.balance))}</span></>
            ) : (
              'الحساب مُسوّى تماماً'
            )}
          </Alert>

          <TableWrapper>
            <Table>
              <Thead>
                <tr>
                  <Th>التاريخ</Th>
                  <Th>البيان</Th>
                  <Th>المرجع</Th>
                  <Th align="end">مدين (علينا)</Th>
                  <Th align="end">دائن (دفعنا)</Th>
                  <Th align="end">الرصيد</Th>
                </tr>
              </Thead>
              <Tbody>
                {data.openingBalance !== 0 && (
                  <Tr>
                    <Td className="text-xs text-fg-muted">—</Td>
                    <Td className="font-medium text-fg">رصيد افتتاحي</Td>
                    <Td>—</Td>
                    <Td align="end" className="tabular-nums" dir="ltr">{formatMoney(data.openingBalance)}</Td>
                    <Td align="end">—</Td>
                    <Td align="end" className="font-semibold tabular-nums" dir="ltr">
                      {formatMoney(data.openingBalance)}
                    </Td>
                  </Tr>
                )}
                {data.entries.map((e) => (
                  <Tr key={`${e.kind}-${e.id}`}>
                    <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(e.at)}</Td>
                    <Td>
                      <Badge tone={e.kind === 'purchase' ? 'warning' : 'success'}>
                        {e.kind === 'purchase' ? 'فاتورة شراء' : 'دفعة'}
                      </Badge>
                    </Td>
                    <Td className="font-mono text-xs text-fg-muted" dir="ltr">{e.reference}</Td>
                    <Td align="end" className="tabular-nums" dir="ltr">
                      {e.debit > 0 ? formatMoney(e.debit) : '—'}
                    </Td>
                    <Td align="end" className="tabular-nums" dir="ltr">
                      {e.credit > 0 ? formatMoney(e.credit) : '—'}
                    </Td>
                    <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(e.balance)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {data.entries.length === 0 && data.openingBalance === 0 && (
              <EmptyState icon={<FileText className="size-9" />} title="لا توجد حركات على هذا المورّد" />
            )}
          </TableWrapper>
        </div>
      )}
    </Dialog>
  )
}

export function SuppliersPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.SUPPLIER_MANAGE)

  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState<string | undefined>()
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [statementId, setStatementId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<SupplierListItem | null>(null)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['suppliers', { page, search }],
    queryFn: () => suppliersApi.list({ page, pageSize: 25, search }),
    placeholderData: keepPreviousData,
  })

  const { data: editing } = useQuery({
    queryKey: ['suppliers', 'detail', editingId],
    queryFn: () => suppliersApi.get(editingId!),
    enabled: !!editingId,
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => suppliersApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      toast.success('تم حذف المورّد')
      setDeleting(null)
    },
    onError: (err) => {
      toast.error('تعذّر الحذف', err instanceof Error ? err.message : undefined)
      setDeleting(null)
    },
  })

  const totalDebt = data?.items.reduce((sum, s) => sum + Math.max(0, s.balance), 0) ?? 0

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">الموردون</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {data ? `${formatNumber(data.total)} مورّد` : 'جارٍ التحميل…'}
            {totalDebt > 0 && (
              <> · مستحقات <span className="font-medium text-warning" dir="ltr">{formatMoney(totalDebt)}</span></>
            )}
          </p>
        </div>
        {canManage && (
          <Button startIcon={<Plus className="size-4" />} onClick={() => { setEditingId(null); setFormOpen(true) }}>
            مورّد جديد
          </Button>
        )}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim() || undefined); setPage(1) }}
      >
        <Input
          placeholder="ابحث بالاسم أو الرمز أو الهاتف…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          startIcon={<Search className="size-4" />}
          endIcon={
            searchInput ? (
              <button
                type="button"
                onClick={() => { setSearchInput(''); setSearch(undefined); setPage(1) }}
                className="pointer-events-auto rounded p-0.5 text-fg-subtle hover:text-fg"
                aria-label="مسح"
              >
                <X className="size-4" />
              </button>
            ) : undefined
          }
        />
        <Button type="submit" variant="secondary">بحث</Button>
      </form>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>المورّد</Th>
              <Th>الرمز</Th>
              <Th>الهاتف</Th>
              <Th align="center">الفواتير</Th>
              <Th align="end">الرصيد</Th>
              <Th align="center">إجراءات</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={6} cols={6} />
          ) : (
            <Tbody>
              {data?.items.map((s) => (
                <Tr key={s.id} className={cn(!s.isActive && 'opacity-55')}>
                  <Td>
                    <div className="flex items-center gap-3">
                      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-bg-subtle text-fg-subtle">
                        <Truck className="size-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-fg">{s.nameAr}</p>
                        {s.contactName && <p className="text-xs text-fg-subtle">{s.contactName}</p>}
                      </div>
                      {!s.isActive && <Badge tone="neutral" className="text-[10px]">معطّل</Badge>}
                    </div>
                  </Td>
                  <Td className="font-mono text-xs text-fg-muted" dir="ltr">{s.code}</Td>
                  <Td className="text-sm text-fg-muted" dir="ltr">{s.phone ?? '—'}</Td>
                  <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(s.purchaseCount)}</Td>
                  <Td align="end">
                    <span
                      className={cn(
                        'font-semibold tabular-nums',
                        s.balance > 0 ? 'text-warning' : s.balance < 0 ? 'text-info' : 'text-fg-muted',
                      )}
                      dir="ltr"
                    >
                      {formatMoney(s.balance)}
                    </span>
                  </Td>
                  <Td align="center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="icon" title="كشف الحساب" onClick={() => setStatementId(s.id)}>
                        <Wallet className="size-4" />
                      </Button>
                      {canManage && (
                        <>
                          <Button
                            variant="ghost" size="icon" title="تعديل"
                            onClick={() => { setEditingId(s.id); setFormOpen(true) }}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" title="حذف"
                            className="text-danger hover:bg-danger-subtle"
                            onClick={() => setDeleting(s)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<Truck className="size-10" />}
            title={search ? 'لا توجد نتائج' : 'لا يوجد موردون'}
            description={search ? 'جرّب بحثاً آخر' : 'أضف الموردين الذين تشتري منهم'}
            action={
              canManage && !search ? (
                <Button onClick={() => { setEditingId(null); setFormOpen(true) }}>إضافة مورّد</Button>
              ) : undefined
            }
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            pageSize={data.pageSize}
            onPageChange={setPage}
          />
        )}
      </TableWrapper>

      <SupplierFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingId(null) }}
        supplier={editingId ? (editing ?? null) : null}
      />

      <StatementDialog supplierId={statementId} onClose={() => setStatementId(null)} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && removeMutation.mutate(deleting.id)}
        loading={removeMutation.isPending}
        title="حذف المورّد"
        confirmLabel="حذف"
        description={
          <>
            سيُحذف <span className="font-semibold text-fg">{deleting?.nameAr}</span>.
            <br />
            لا يمكن حذف مورّد له فواتير — عطّله بدل ذلك.
          </>
        }
      />
    </div>
  )
}
